import { color } from '@ahamove/design-tokens';
import Box from '@ahamove/web-components/lib/Box';
import Tab, { tabClasses } from '@ahamove/web-components/lib/Tab';
import Tabs, { tabsClasses } from '@ahamove/web-components/lib/Tabs';
import Typography from '@ahamove/web-components/lib/Typography';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import type { ServiceModalData } from '@/api/service/types';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { useGetServiceModels } from '@/api/service/useGetServiceModels';
import useIsMobile from '@/shared-hooks/useIsMobile';
import EmptyServiceModels from '../booking/EmptyServiceModels';
import { homeConfig } from './homeConfig';
import ServicePriceSlider from './ServicePriceSlider';

type Props = {
  defaultServiceList: ServiceModalData[];
  availableCities?: AvailableCity[];
  modal: boolean;
  shouldRefresh: boolean;
};

const cities: Record<string, string> = {
  DAD: 'Đà Nẵng',
  SGN: 'Hồ Chí Minh',
  HAN: 'Hà Nội',
  VKG: 'Kiên Giang',
};

export default function ServicesPriceContent({
  defaultServiceList,
  availableCities,
  modal = false,
  shouldRefresh = false,
}: Partial<Props>) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const [value, setValue] = useState('DAD');
  const [currentSlide, setCurrentSlide] = useState(0);
  const { locale } = useRouter();
  const isMobile = useIsMobile();
  const { data: listService, isLoading } = useGetServiceModels(
    {
      city_id: value,
    },
    {
      cacheTime: 10000,
    }
  );
  const handleTabChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    setValue(newValue);
    setCurrentSlide(0);
    // queryClient.invalidateQueries([
    //   apiRoutes.services.service_models,
    //   {
    //     city_id: newValue,
    //   },
    // ]);
  };

  return (
    <Box
      sx={{
        pt: isMobile ? 3 : 8,
        pb: isMobile ? 3 : 8,
        borderRadius: isMobile ? '8px' : '24px',
        backgroundColor: color.neutral[10],
      }}
      data-test-id="servicesPriceContent"
    >
      {!modal ? (
        <>
          <Typography
            variant="h2"
            fontSize={isMobile ? 32 : 48}
            lineHeight={isMobile ? '40px' : '56px'}
            color={color.neutral[90]}
            textAlign="center"
            mb={isMobile ? 1 : 1.5}
          >
            {t('common:price_table.title')}
          </Typography>
          <Typography
            variant="body1"
            fontWeight={500}
            fontSize={isMobile ? 14 : 16}
            lineHeight={isMobile ? '20px' : '24px'}
            color={color.neutral[60]}
            textAlign="center"
          >
            {t('common:price_table.desc')}
          </Typography>
          {availableCities ? (
            <Tabs
              classes={{
                root: {
                  justifyContent: 'center',
                },
              }}
              sx={{
                px: isMobile ? 1 : 2,
                justifyContent: 'center',
                [`.${tabClasses.root}`]: {
                  color: '#777E90',
                  fontWeight: 700,
                  minHeight: isMobile ? '36px' : '56px',
                  fontSize: isMobile ? 12 : 20,
                  lineHeight: isMobile ? '20px' : '32px',
                  py: isMobile ? 1 : 1.5,
                  px: isMobile ? 1.5 : 4.25,
                  mb: 3,

                  '&.Mui-selected': {
                    color: color.blue[60],
                    borderRadius: '100px',
                    boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
                    backgroundColor: '#FCFCFD',
                  },
                },
                [`.${tabsClasses.scroller}`]: {
                  flexGrow: 0,
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
              value={value}
              onChange={(event, value) => handleTabChange(event, value)}
              TabIndicatorProps={{
                style: { display: 'none' },
              }}
            >
              {availableCities.map((item, idx) => (
                <Tab
                  sx={{
                    '&.MuiTab-root': {
                      mt: !modal ? 4 : 0,
                      mb: 4,
                    },
                  }}
                  index={idx}
                  value={item._id}
                  label={locale === 'vi' ? item.name_vi_vn : item.name}
                  key={idx}
                />
              ))}
            </Tabs>
          ) : null}
        </>
      ) : null}
      {modal && defaultServiceList && defaultServiceList.length > 0 ? (
        <Tabs
          sx={{
            [`.${tabClasses.root}`]: {
              color: '#777E90',
              fontWeight: 700,
              minHeight: isMobile ? '36px' : '56px',
              fontSize: isMobile ? 12 : 20,
              lineHeight: isMobile ? '20px' : '32px',
              py: isMobile ? 1 : 1.5,
              px: isMobile ? 1.5 : 4.25,
              mb: 3,

              '&.Mui-selected': {
                color: color.blue[60],
                borderRadius: '100px',
                boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
                backgroundColor: '#FCFCFD',
              },
            },
          }}
          value={defaultServiceList[0].city_id}
          centered
          TabIndicatorProps={{
            style: { display: 'none' },
          }}
        >
          <Tab
            index={0}
            value={defaultServiceList[0].city_id}
            label={
              availableCities?.find(
                (item) => item._id === defaultServiceList[0].city_id
              )
                ? locale === 'vi'
                  ? availableCities?.find(
                      (item) => item._id === defaultServiceList[0].city_id
                    )?.name_vi_vn
                  : availableCities?.find(
                      (item) => item._id === defaultServiceList[0].city_id
                    )?.name
                : cities[defaultServiceList[0].city_id]
            }
          />
        </Tabs>
      ) : null}
      {modal && defaultServiceList ? (
        <ServicePriceSlider
          shouldRefresh={shouldRefresh}
          listService={defaultServiceList}
          currentSlide={currentSlide}
          changeCurrentSlide={(value: number) => setCurrentSlide(value)}
        />
      ) : availableCities &&
        listService &&
        listService.length > 0 &&
        !isLoading ? (
        <ServicePriceSlider
          listService={listService}
          currentSlide={currentSlide}
          changeCurrentSlide={(value: number) => setCurrentSlide(value)}
        />
      ) : isLoading ? (
        <div className=" mx-auto flex h-full min-h-[50vh] w-full items-center justify-center">
          <button
            type="button"
            className="text-body16 text-secondary-60 inline-flex cursor-not-allowed items-center px-4 py-2 font-semibold transition duration-150 ease-in-out"
            disabled
          >
            <svg
              className="text-primary-50 -ml-1 mr-3 h-6 w-6 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Đang tải bảng giá...
          </button>
        </div>
      ) : (
        <EmptyServiceModels title="Không tìm thấy bảng giá thuê trong khu vực này" />
      )}
    </Box>
  );
}
